import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, RefreshCw, QrCode, Loader2, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

type ConnState = "open" | "close" | "connecting" | "unknown";

interface InstanceRow {
  name: string;
  phone_number: string | null;
}

function stateLabel(s: ConnState) {
  if (s === "open") return { label: "Conectado", variant: "default" as const, color: "bg-emerald-500" };
  if (s === "connecting") return { label: "Conectando…", variant: "secondary" as const, color: "bg-amber-500" };
  if (s === "close") return { label: "Desconectado", variant: "destructive" as const, color: "bg-red-500" };
  return { label: "Desconhecido", variant: "outline" as const, color: "bg-muted" };
}

async function invokeRestaurantWa(action: string, restaurantId: string, extra: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke("restaurant-whatsapp", {
    body: { action, restaurantId, ...extra },
  });
  if (error) throw error;
  const r = data as { ok: boolean; status: number; data: any; instance?: string };
  if (!r.ok) {
    const msg = r.data?.error || r.data?.message || `HTTP ${r.status}`;
    throw new Error(msg);
  }
  return r;
}

export function RestaurantWhatsAppStatus() {
  const { activeRestaurant } = useRestaurantContext();
  const restaurantId = activeRestaurant?.id;

  const [instance, setInstance] = useState<InstanceRow | null>(null);
  const [loadingInstance, setLoadingInstance] = useState(false);
  const [state, setState] = useState<ConnState>("unknown");
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [qrBase64, setQrBase64] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  // Load bound instance via secure RPC
  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    (async () => {
      setLoadingInstance(true);
      try {
        const { data, error } = await supabase.rpc("get_restaurant_evolution_instance", {
          p_restaurant_id: restaurantId,
        });
        if (error) throw error;
        if (cancelled) return;
        const row = Array.isArray(data) ? data[0] : (data as any);
        if (row?.name) setInstance({ name: row.name, phone_number: row.phone_number ?? null });
        else setInstance(null);
      } catch (e: any) {
        console.error(e);
        setInstance(null);
      } finally {
        setLoadingInstance(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId]);

  const refreshStatus = useCallback(async () => {
    if (!restaurantId || !instance) return;
    setLoadingStatus(true);
    try {
      const res = await invokeRestaurantWa("status", restaurantId);
      const s = (res.data?.instance?.state as ConnState) ?? "unknown";
      setState(s);
      if (s === "open" && qrBase64) {
        setQrBase64(null);
        toast.success("WhatsApp conectado!");
      }
    } catch (e: any) {
      // silent: don't spam errors on poll
      console.warn("WhatsApp status:", e.message);
    } finally {
      setLoadingStatus(false);
    }
  }, [restaurantId, instance, qrBase64]);

  useEffect(() => {
    if (!instance) return;
    refreshStatus();
    const id = window.setInterval(refreshStatus, 7000);
    return () => window.clearInterval(id);
  }, [refreshStatus, instance]);

  const handleConnect = async () => {
    if (!restaurantId) return;
    setBusy("qr");
    try {
      const res = await invokeRestaurantWa("connect", restaurantId);
      const d = res.data || {};
      const b64: string | undefined = d.base64 || d?.qrcode?.base64;
      if (b64) {
        const normalized = b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`;
        setQrBase64(normalized);
      } else {
        toast.error("Sem QR Code na resposta. Tente novamente em alguns segundos.");
      }
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = window.setInterval(refreshStatus, 3000);
      window.setTimeout(() => {
        if (pollRef.current) {
          window.clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 120000);
    } catch (e: any) {
      toast.error(`Falha ao gerar QR: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  if (loadingInstance) {
    return (
      <div className="rounded-lg border p-4 text-sm text-muted-foreground">
        Carregando informações do WhatsApp…
      </div>
    );
  }

  if (!instance) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Nenhuma instância WhatsApp foi vinculada a este restaurante ainda.
          Solicite ao suporte (super admin) a criação da sua instância — você
          escaneia o QR Code aqui assim que ela for liberada.
        </AlertDescription>
      </Alert>
    );
  }

  const sl = stateLabel(state);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              WhatsApp do Restaurante
            </CardTitle>
            <CardDescription>
              Instância dedicada — a chave da API fica protegida no servidor.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={refreshStatus} disabled={loadingStatus}>
            {loadingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`h-3 w-3 rounded-full ${sl.color}`} />
          <Badge variant={sl.variant}>{sl.label}</Badge>
          <span className="text-sm text-muted-foreground">
            Instância: <code className="bg-muted px-1.5 py-0.5 rounded">{instance.name}</code>
          </span>
          {instance.phone_number && (
            <span className="text-sm text-muted-foreground">
              · Número: <code className="bg-muted px-1.5 py-0.5 rounded">{instance.phone_number}</code>
            </span>
          )}
        </div>

        {state !== "open" && (
          <div className="space-y-3">
            <Button onClick={handleConnect} disabled={busy === "qr"}>
              {busy === "qr" ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
              <span className="ml-2">Gerar QR Code</span>
            </Button>
            {qrBase64 && (
              <div className="flex flex-col items-center gap-2 rounded-lg border p-4 bg-muted/30">
                <img src={qrBase64} alt="QR Code WhatsApp" className="h-56 w-56" />
                <p className="text-xs text-muted-foreground text-center">
                  WhatsApp → Configurações → Aparelhos conectados → Conectar um aparelho
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}